import type { Experiment } from 'types';
import { cn } from 'helpers/cn';

type ScheduleBadgeProps = { experiment: Experiment };

const CHIP_CLASS = 'micro-label rounded-pill inline-flex items-center border px-2 py-0.5';

/**
 * Tone follows the state, not the fact that a schedule exists.
 *
 * A start or stop date is a plain fact, so it stays on the hairline and reads as
 * supporting detail under the status chip. A hold is a decision someone has to make
 * (warn), and a blocked automation is broken (danger) — those are the only two that
 * are allowed to draw the eye.
 */
const CHIP_TONE = {
  held: 'border-warn-line text-warn',
  blocked: 'border-danger-line text-danger',
  scheduled: 'border-line text-dim',
} as const;

/**
 * "Scheduled" is derived, not a status.
 *
 * Adding it to EXPERIMENT_STATUSES would have meant an ALTER TYPE plus auditing every
 * status filter in the codebase — the runtime artifact query, four cron consumers, the
 * implementation list — and any filter that means "not yet live" but is written as
 * status = 'Draft' would have broken silently. Archived is already handled this way, as
 * a view over a nullable column rather than a status, so this follows it.
 *
 * The side effect is the useful one: a scheduled experiment stays Draft, and Draft is
 * excluded from the runtime artifact, so it cannot be served early by construction.
 */
export const ScheduleBadge = ({ experiment }: ScheduleBadgeProps) => {
  const schedule = experiment.schedule;
  if (!schedule) return null;

  if (schedule.state === 'held') {
    const days = schedule.heldSince ? Math.floor((Date.now() - schedule.heldSince) / (24 * 60 * 60 * 1000)) : 0;
    return (
      <span className={cn(CHIP_CLASS, CHIP_TONE.held)} title={schedule.blockedReason ?? undefined}>
        Held{days > 0 ? ` ${days}d` : ''}
      </span>
    );
  }

  if (schedule.state === 'blocked') {
    return (
      <span className={cn(CHIP_CLASS, CHIP_TONE.blocked)} title={schedule.blockedReason ?? undefined}>
        Needs attention
      </span>
    );
  }

  if (schedule.state === 'armed' && schedule.startAt) {
    return (
      <span className={cn(CHIP_CLASS, CHIP_TONE.scheduled)}>
        Starts {new Date(schedule.startAt).toLocaleDateString()}
      </span>
    );
  }

  if (schedule.state === 'active' && schedule.stopAt) {
    return (
      <span className={cn(CHIP_CLASS, CHIP_TONE.scheduled)}>Ends {new Date(schedule.stopAt).toLocaleDateString()}</span>
    );
  }

  return null;
};
